import {
  FILTER_OP_SHAPES,
  FieldPath,
  FilterExpr,
  FilterOpName,
  LabelLiteral,
  LabelLiterals,
  LabelRange,
  LabelText,
  LiteralRange,
  OpArgument,
  OpShape,
  Quantifier
} from './wire';

interface DraftBase {
  id: string;
  negated: boolean;
}

export interface DraftGroup extends DraftBase {
  kind: 'group';
  connector: 'and' | 'or';
  children: DraftNode[];
}

export interface DraftAssociation extends DraftBase {
  kind: 'association';
  edge: string;
  quantifier: Quantifier;
  filter: DraftGroup;
}

export interface DraftCondition extends DraftBase {
  kind: 'condition';
  path: FieldPath | null;
  op: FilterOpName | null;
  arg: OpArgument | null;
}

/** A wire node the current vocabulary cannot represent; shown read-only and kept verbatim. */
export interface DraftOpaque extends DraftBase {
  kind: 'opaque';
  expr: FilterExpr;
}

export type DraftNode = DraftGroup | DraftAssociation | DraftCondition | DraftOpaque;

export function newDraftId(): string {
  return crypto.randomUUID();
}

export function draftGroup(
  connector: 'and' | 'or' = 'and',
  children: DraftNode[] = []
): DraftGroup {
  return { kind: 'group', id: newDraftId(), negated: false, connector, children };
}

export function draftCondition(
  path: FieldPath | null = null,
  op: FilterOpName | null = null,
  arg: OpArgument | null = null
): DraftCondition {
  return { kind: 'condition', id: newDraftId(), negated: false, path, op, arg };
}

export function draftAssociation(
  edge: string,
  quantifier: Quantifier = 'any'
): DraftAssociation {
  return {
    kind: 'association',
    id: newDraftId(),
    negated: false,
    edge,
    quantifier,
    filter: draftGroup()
  };
}

export function draftOpaque(expr: FilterExpr): DraftOpaque {
  return { kind: 'opaque', id: newDraftId(), negated: false, expr };
}

export function isConditionComplete(condition: DraftCondition): boolean {
  if (!condition.path || !condition.op) return false;
  const shape = FILTER_OP_SHAPES[condition.op];
  return shape === 'none' || isArgumentComplete(shape, condition.arg);
}

function isArgumentComplete(
  shape: Exclude<OpShape, 'none'>,
  arg: OpArgument | null
): boolean {
  if (arg === null || arg === undefined) return false;
  switch (shape) {
    case 'literal':
    case 'relative_date':
      return true;
    case 'range': {
      const range = arg as LiteralRange;
      return range.lo != null && range.hi != null;
    }
    case 'literals':
    case 'texts':
      return (arg as unknown[]).length > 0;
    case 'text':
      return (arg as string).length > 0;
    case 'label_literal': {
      const value = arg as LabelLiteral;
      return value.label.length > 0 && value.value != null;
    }
    case 'label_literals': {
      const value = arg as LabelLiterals;
      return value.label.length > 0 && value.values.length > 0;
    }
    case 'label_range': {
      const value = arg as LabelRange;
      return value.label.length > 0 && value.lo != null && value.hi != null;
    }
    case 'label_text': {
      const value = arg as LabelText;
      return value.label.length > 0 && value.value.length > 0;
    }
  }
}

/** Replaces the node `id` (anywhere, associations included) by `update(node)`; `null` removes it. */
function mapNode(
  node: DraftNode,
  id: string,
  update: (node: DraftNode) => DraftNode | null
): DraftNode | null {
  if (node.id === id) return update(node);
  if (node.kind === 'group') {
    return {
      ...node,
      children: node.children
        .map((child) => mapNode(child, id, update))
        .filter((child): child is DraftNode => child !== null)
    };
  }
  if (node.kind === 'association') {
    const filter = mapNode(node.filter, id, update);
    if (filter === null) return { ...node, filter: draftGroup() };
    return { ...node, filter: filter.kind === 'group' ? filter : node.filter };
  }
  return node;
}

/** An update that would turn the root or an association's filter into a non-group is ignored. */
export function updateDraft(
  root: DraftGroup,
  id: string,
  update: (node: DraftNode) => DraftNode
): DraftGroup {
  const next = mapNode(root, id, update);
  return next?.kind === 'group' ? next : root;
}

export function removeDraftNode(root: DraftGroup, id: string): DraftGroup {
  if (root.id === id) return draftGroup(root.connector);
  return mapNode(root, id, () => null) as DraftGroup;
}

export function appendDraftChild(
  root: DraftGroup,
  groupId: string,
  child: DraftNode
): DraftGroup {
  return updateDraft(root, groupId, (node) =>
    node.kind === 'group' ? { ...node, children: [...node.children, child] } : node
  );
}

/**
 * ANDs `child` with the whole filter. Appending into an OR root or under a negated root would
 * widen what the filter matches; an empty root filters nothing, so it simply becomes `child`.
 */
export function andChild(root: DraftGroup, child: DraftNode): DraftGroup {
  if (root.children.length === 0) return draftGroup('and', [child]);
  if (root.connector === 'and' && !root.negated) {
    return { ...root, children: [...root.children, child] };
  }
  return draftGroup('and', [root, child]);
}

/**
 * ORs `child` with the whole filter, so whatever it matched stays matched. Appending into an AND
 * root or under a negated root would narrow it instead; an empty root already matches every row,
 * so it is left as it is.
 */
export function orChild(root: DraftGroup, child: DraftNode): DraftGroup {
  if (root.children.length === 0) return root;
  if (root.connector === 'or' && !root.negated) {
    return { ...root, children: [...root.children, child] };
  }
  return draftGroup('or', [root, child]);
}

export function findDraftNode(root: DraftNode, id: string): DraftNode | undefined {
  if (root.id === id) return root;
  if (root.kind === 'group') {
    for (const child of root.children) {
      const found = findDraftNode(child, id);
      if (found) return found;
    }
  }
  if (root.kind === 'association') return findDraftNode(root.filter, id);
  return undefined;
}

/**
 * Leaves negation only where the composer shows it: on `or` groups (None) and `any` blocks (No).
 * Any other negated node moves into a None group holding just that node, which encodes to the same
 * `not`. A block's `and` filter stays the block's body and holds that None group instead.
 */
export function liftNegations(root: DraftGroup): DraftGroup {
  const group = liftChildren(root);
  return needsNoneGroup(group) ? noneOf(group) : group;
}

function liftChildren(group: DraftGroup): DraftGroup {
  return { ...group, children: group.children.map(liftNode) };
}

function liftNode(node: DraftNode): DraftNode {
  if (node.kind === 'group') return liftNegations(node);
  const lifted =
    node.kind === 'association' ? { ...node, filter: liftFilter(node.filter) } : node;
  return needsNoneGroup(lifted) ? noneOf(lifted) : lifted;
}

function liftFilter(filter: DraftGroup): DraftGroup {
  const group = liftChildren(filter);
  return needsNoneGroup(group) ? draftGroup('and', [noneOf(group)]) : group;
}

function needsNoneGroup(node: DraftNode): boolean {
  if (!node.negated) return false;
  if (node.kind === 'group') return node.connector !== 'or';
  if (node.kind === 'association') return node.quantifier !== 'any';
  return true;
}

function noneOf(node: DraftNode): DraftGroup {
  return { ...draftGroup('or', [{ ...node, negated: false }]), negated: true };
}

/** True when the draft is a plain AND of plain conditions — the shape the pills bar can show. */
export function isFlatDraft(root: DraftGroup): boolean {
  return (
    root.connector === 'and' &&
    !root.negated &&
    root.children.every((child) => child.kind === 'condition' && !child.negated)
  );
}

/** Leaf conditions (plain and opaque), counted through groups and associations. */
export function countDraftConditions(node: DraftNode): number {
  switch (node.kind) {
    case 'group':
      return node.children.reduce((sum, child) => sum + countDraftConditions(child), 0);
    case 'association':
      // An empty sub-filter still filters: "has at least one related row".
      return Math.max(1, countDraftConditions(node.filter));
    default:
      return 1;
  }
}
